import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const ReturnsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Button onClick={() => navigate('/')} variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a la tienda
          </Button>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h1 className="text-3xl font-bold mb-6 text-gray-800">Política de Devoluciones y Cambios</h1>
            <p className="text-sm text-gray-500 mb-8">Última actualización: Febrero 2025</p>

            {/* Resumen visual */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <div className="text-3xl mb-2">🔄</div>
                <h3 className="font-bold text-green-800">Cambio de Talla</h3>
                <p className="text-green-600 text-lg font-bold">GRATIS</p>
                <p className="text-sm text-green-700">15 días</p>
              </div>
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 text-center">
                <div className="text-3xl mb-2">🎁</div>
                <h3 className="font-bold text-purple-800">Tarjeta Regalo</h3>
                <p className="text-black text-lg font-bold">GRATIS</p>
                <p className="text-sm text-black">Valor total</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <div className="text-3xl mb-2">💶</div>
                <h3 className="font-bold text-blue-800">Reembolso</h3>
                <p className="text-blue-600 text-lg font-bold">85%</p>
                <p className="text-sm text-blue-700">15% gastos gestión</p>
              </div>
            </div>

            <div className="prose prose-gray max-w-none space-y-6">
              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">1. Derecho de Desistimiento</h2>
                <p className="text-gray-600">
                  De acuerdo con la legislación vigente en España, tienes derecho a desistir de tu compra en un plazo de <strong>14 días naturales</strong> desde la recepción del producto, sin necesidad de justificación.
                </p>
                <p className="text-gray-600 mt-2">
                  Además, en JRF SPORTSWEAR ampliamos este plazo a <strong>15 días</strong> para tu comodidad.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">2. Condiciones para la Devolución</h2>
                <p className="text-gray-600">Para que podamos aceptar una devolución o cambio:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-1 mt-3">
                  <li>El producto debe estar <strong>sin usar</strong></li>
                  <li>Debe conservar las <strong>etiquetas originales</strong></li>
                  <li>Debe estar en su <strong>embalaje original</strong> o similar</li>
                  <li>No debe tener daños, manchas o alteraciones</li>
                  <li>Debe incluir todos los accesorios (si los hubiera)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">3. Opciones Disponibles</h2>
                
                <div className="bg-green-50 rounded-lg p-4 mt-4">
                  <h3 className="font-bold text-green-800 mb-2">🔄 Opción 1: Cambio de Talla o Producto (GRATIS)</h3>
                  <p className="text-gray-600">
                    Si la talla no te queda bien o prefieres otro producto, lo cambiamos sin coste adicional.
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 text-sm mt-2">
                    <li>Te enviamos etiqueta de devolución prepagada</li>
                    <li>Una vez recibido, enviamos el nuevo producto</li>
                    <li>Si hay diferencia de precio, te avisamos antes</li>
                  </ul>
                </div>

                <div className="bg-neutral-50 rounded-lg p-4 mt-4">
                  <h3 className="font-bold text-purple-800 mb-2">🎁 Opción 2: Tarjeta Regalo (GRATIS)</h3>
                  <p className="text-gray-600">
                    Cambia tu producto por una Tarjeta Regalo del <strong>valor total</strong> de tu compra.
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 text-sm mt-2">
                    <li>La tarjeta no caduca</li>
                    <li>Válida para cualquier producto</li>
                    <li>Te la enviamos por email en 24-48h tras recibir el producto</li>
                    <li>Opción ideal si no tienes claro qué quieres</li>
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 mt-4">
                  <h3 className="font-bold text-blue-800 mb-2">💶 Opción 3: Reembolso (con 15% de retención)</h3>
                  <p className="text-gray-600">
                    Si prefieres que te devolvamos el dinero, reembolsamos el <strong>85% del importe</strong>. El 15% restante cubre los gastos de gestión, logística y procesamiento.
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 text-sm mt-2">
                    <li>El reembolso se procesa en 5-7 días laborables</li>
                    <li>Se devuelve por el mismo método de pago utilizado</li>
                    <li>Los gastos de envío originales no son reembolsables</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">4. Cómo Solicitar una Devolución o Cambio</h2>
                <ol className="list-decimal pl-6 text-gray-600 space-y-2">
                  <li>
                    <strong>Contacta con nosotros</strong> por email (infojrfsportswear@gmail.com) o WhatsApp indicando:
                    <ul className="list-disc pl-6 mt-1">
                      <li>Número de pedido</li>
                      <li>Producto(s) que deseas devolver</li>
                      <li>Motivo (opcional)</li>
                      <li>Opción elegida (cambio, tarjeta regalo o reembolso)</li>
                    </ul>
                  </li>
                  <li><strong>Te enviamos</strong> la etiqueta de devolución prepagada por email</li>
                  <li><strong>Empaqueta</strong> el producto de forma segura</li>
                  <li><strong>Entrega</strong> el paquete en el punto indicado</li>
                  <li><strong>Procesamos</strong> tu solicitud en 24-48h tras recibir el producto</li>
                </ol>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">5. Productos No Admitidos</h2>
                <p className="text-gray-600">No se admiten devoluciones de:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-1 mt-2">
                  <li>Productos usados o lavados</li>
                  <li>Productos sin etiquetas originales</li>
                  <li>Productos dañados por mal uso</li>
                  <li>Ropa interior o bañadores por razones de higiene (salvo defecto de fábrica)</li>
                  <li>Productos personalizados</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">6. Productos Defectuosos</h2>
                <p className="text-gray-600">
                  Si el producto tiene un defecto de fabricación o no es conforme con lo contratado:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-1 mt-2">
                  <li><strong>Reembolso del 100%</strong> o sustitución del producto</li>
                  <li>Gastos de envío a nuestro cargo</li>
                  <li>Garantía legal de 3 años</li>
                </ul>
                <p className="text-gray-600 mt-2">
                  Por favor, envíanos fotos del defecto para agilizar el proceso.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-800 mb-3">7. Contacto</h2>
                <p className="text-gray-600">
                  ¿Tienes dudas? Estamos aquí para ayudarte:
                </p>
                <ul className="list-none text-gray-600 space-y-2 mt-3">
                  <li>📧 <strong>Email:</strong> infojrfsportswear@gmail.com</li>
                  <li>💬 <strong>WhatsApp:</strong> +34 611 871 722</li>
                  <li>🕐 <strong>Horario:</strong> Lunes a Viernes, 9:00 - 18:00</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ReturnsPage;
